import {
  Component,
  ViewChild,
  ElementRef,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { IlatLng, IMapAdress } from 'src/app/model/google-map.model';

@Component({
    selector: 'app-google-geolocation',
    imports: [],
    templateUrl: './google-geolocation.component.html',
    styleUrls: ['./google-geolocation.component.css']
})
export class GoogleGeolocationComponent {
  @Input() height: string = '500px'; // Default height
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  private _selectedLocation!: { lat: number; lng: number };
  @Input()
  set selectedLocation(value: { lat: number; lng: number }) {
    if (value) {
      this._selectedLocation = value;
      this.updateMapLocation(value);
    }
  }
  get selectedLocation(): { lat: number; lng: number } {
    return this._selectedLocation;
  }

  @Output() formatedMarkerAddress = new EventEmitter<IMapAdress>();

  map!: google.maps.Map;
  marker!: google.maps.marker.AdvancedMarkerElement;
  geocoder!: google.maps.Geocoder;
  autocomplete!: google.maps.places.Autocomplete;

  currentLatLngView: any;
  // defaultLocation = { lat: 17.4065, lng: 78.4772 };
  defaultLocation = {
    lat: 16.235168791444224,
    lng: 80.0519509895105
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedLocation'] && changes['selectedLocation'].currentValue) {
      const newLocation = changes['selectedLocation'].currentValue;
      if (
        newLocation.lat !== this.defaultLocation.lat ||
        newLocation.lng !== this.defaultLocation.lng
      ) {
        this.updateMapLocation(newLocation);
      }
    }
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  initMap() {
    const mapContainer = this.mapContainer?.nativeElement;
    if (!mapContainer) return;

    const initializeMap = (location: { lat: number; lng: number }) => {
      // Initialize the map
      this.map = new google.maps.Map(mapContainer, {
        center: new google.maps.LatLng(location.lat, location.lng),
        zoom: 14,
        mapId: 'MARKER_MAP_ID',
      });

      // Initialize the draggable marker
      this.marker = new google.maps.marker.AdvancedMarkerElement({
        position: location,
        map: this.map,
        gmpDraggable: true,
      });

      // Initialize autocomplete or other features
      this.initializeAutocomplete();
    };

    const handleGeolocationError = (error: GeolocationPositionError) => {
      console.error('Error getting user location:', error);
      let errorMessage: string;

      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Permission denied. Using default location.';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Position unavailable. Using default location.';
          break;
        case error.TIMEOUT:
          errorMessage = 'Geolocation request timed out. Using default location.';
          break;
        default:
          errorMessage = 'An unknown error occurred. Using default location.';
          break;
      }

      console.warn(errorMessage);

      // Use selected or default location in case of error
      const fallbackLocation = this.selectedLocation || this.defaultLocation;
      initializeMap(fallbackLocation);
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = this.selectedLocation || {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          initializeMap(userLocation);
        },
        handleGeolocationError,
        {
          enableHighAccuracy: true, // Request high accuracy if possible
          timeout: 5000, // Set timeout for geolocation
          maximumAge: 0, // Avoid using cached positions
        }
      );
    } else {
      console.warn('Geolocation is not supported by this browser. Using default location.');
      const fallbackLocation = this.selectedLocation || this.defaultLocation;
      initializeMap(fallbackLocation);
    }
  }

  updateMapLocation(location: { lat: number; lng: number }) {
    if (this.map && this.marker) {
      this.map.setCenter(location);
      this.marker.position = location;
    } else {
      this.initMap();
    }
  }

  initializeAutocomplete(): void {

    this.marker.addListener('dragend', () => {
      const position = this.marker.position;
      if (position instanceof google.maps.LatLng) {
        this.getAddressFromCoordinates(position.lat(), position.lng());
      } else if (position) {
        this.getAddressFromCoordinates(position.lat, position.lng);// Convert LatLngLiteral to LatLng
      }
    });

    // Add map click listener
    this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
      const clickedLocation = event.latLng;
      if (clickedLocation) {
        this.marker.position = clickedLocation;
        this.getAddressFromCoordinates(clickedLocation.lat(), clickedLocation.lng());
      }
    });

    const searchInput = document.getElementById('search-input') as HTMLInputElement;
    if (!searchInput) return;

    this.geocoder = new google.maps.Geocoder();

    this.autocomplete = new google.maps.places.Autocomplete(searchInput, {
      types: ['geocode'], // ['premise'],
      bounds: this.createBoundsAroundLocation(this.currentLatLngView || this.defaultLocation, 5000),
    });

    this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete.getPlace();
      if (place.geometry?.location) {
        const location = place.geometry.location;
        this.updateMapLocation({ lat: location.lat(), lng: location.lng() });
      }
    });
  }

  createBoundsAroundLocation(location: any, radius: number): google.maps.LatLngBounds {
    const circle = new google.maps.Circle({
      center: location,
      radius: radius, // Radius in meters
    });
    return circle.getBounds() as google.maps.LatLngBounds;
  }

  getAddressFromCoordinates(lat: number, lng: number): void {
    const latLng = { lat, lng };
    this.geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        this.formattedAddress(results, latLng);
      } else {
        console.error('Geocoder failed due to:', status);
      }
    });
  }

  formattedAddress(addressData: any, selectedLatLng: any) {
    const value = addressData[0];
    const formatedAddress: any = {
      addressText: value.formatted_address,
      addressHeader: value['address_components'][0]['long_name'],
      state: this.getState(value['address_components']) || '',
    };

    for (const component of value['address_components']) {
      if (component['types']?.includes('postal_code')) {
        formatedAddress.zipcode = component['short_name'];
        break;
      }
    }

    formatedAddress.latLng = [selectedLatLng.lat, selectedLatLng.lng];
    this.formatedMarkerAddress.emit(formatedAddress);
  }

  getState(addressComponents) {
    const state = addressComponents.find(c =>
      c.types.includes("administrative_area_level_1")
    );
    return state ? state.long_name : null;
  }
}
